import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { getDailyAnalytics, getAllUsers, cleanupInvalidDates, getScreenCompletionFrequency } from '../../services/funnelService';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error};
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  line-height: ${theme.typography.lineHeights.normal};
`;

const DebugInfo = styled.div`
  background: ${theme.colors.doryBeige};
  border: 1px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.lg};
  text-align: left;
  
  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthChartContainer = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const ChartCard = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
`;

const ChartTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  text-align: center;
`;

const ChartContainer = styled.div`
  height: 300px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  padding: ${theme.spacing.md} 0;
  border-bottom: 2px solid ${theme.colors.doryLighterBlue};
  margin-bottom: ${theme.spacing.md};
`;

const BarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 20px;
`;

const Bar = styled.div`
  width: 100%;
  background: ${props => props.$isFunnelStarts ? theme.colors.primary : theme.colors.doryBlue};
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  min-height: 4px;
  margin-bottom: ${theme.spacing.xs};
  transition: all ${theme.animation.durations.fast};
  
  &:hover {
    opacity: 0.8;
  }
`;

const BarLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  transform: rotate(-45deg);
  white-space: nowrap;
  margin-top: ${theme.spacing.xs};
`;

const BarValue = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.xs};
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const StatLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
`;

const RefreshButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  margin-bottom: ${theme.spacing.lg};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
  }
`;

const AnalyticsScreen = () => {
  const [analytics, setAnalytics] = useState(null);
  const [screenCompletionFrequency, setScreenCompletionFrequency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const testFirebaseConnection = async () => {
    try {
      const users = await getAllUsers();
      return true;
    } catch (err) {
      console.error('Firebase connection test failed:', err);
      return false;
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    setDebugInfo('');
    try {
      // Test Firebase connection first
      const connectionOk = await testFirebaseConnection();
      if (!connectionOk) {
        throw new Error('Cannot connect to Firebase database');
      }
      
      const [data, frequencyData] = await Promise.all([
        getDailyAnalytics(30),
        getScreenCompletionFrequency()
      ]);
      
      setAnalytics(data);
      setScreenCompletionFrequency(frequencyData);
    } catch (err) {
      const errorMessage = err.message || 'Failed to load analytics data. Please try again.';
      setError(`Error: ${errorMessage}`);
      
      // Add debug information
      const debugDetails = {
        error: err.message,
        stack: err.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      };
      setDebugInfo(JSON.stringify(debugDetails, null, 2));
      
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    setCleanupLoading(true);
    try {
      const result = await cleanupInvalidDates();
      
      // Reload analytics after cleanup
      await loadAnalytics();
    } catch (err) {
      console.error('Cleanup failed:', err);
    } finally {
      setCleanupLoading(false);
    }
  };



  useEffect(() => {
    loadAnalytics();
  }, []);

  const getMaxValue = (data, key) => {
    return Math.max(...Object.values(data).map(day => day[key] || 0));
  };

  const getBarHeight = (value, maxValue, multiplier = 1) => {
    if (maxValue === 0) return 4;
    return Math.max(4, (value / maxValue) * 250 * multiplier);
  };

  const formatDate = (dateString) => {
    // Parse the date string as UTC to avoid timezone conversion issues
    const date = new Date(dateString + 'T00:00:00.000Z');
    
    // Use UTC methods to format the date consistently
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getUTCMonth()];
    const day = date.getUTCDate();
    
    return `${month} ${day}`;
  };

  const calculateTotals = () => {
    if (!analytics) return { totalStarts: 0, totalVisits: 0, averageVisits: 0 };
    
    const totals = Object.values(analytics).reduce((acc, day) => ({
      totalStarts: acc.totalStarts + day.funnelStarts,
      totalVisits: acc.totalVisits + day.totalScreenVisits
    }), { totalStarts: 0, totalVisits: 0 });
    
    totals.averageVisits = totals.totalStarts > 0 ? 
      Math.round((totals.totalVisits / totals.totalStarts) * 100) / 100 : 0;
    
    return totals;
  };

  const calculateOneScreenStats = () => {
    if (!screenCompletionFrequency) return { count: 0, percentage: 0 };
    
    const oneScreenVisits = screenCompletionFrequency[0] || 0;
    const totalVisitors = Object.values(screenCompletionFrequency).reduce((sum, count) => sum + count, 0);
    const percentage = totalVisitors > 0 ? Math.round((oneScreenVisits / totalVisitors) * 100) : 0;
    
    return { count: oneScreenVisits, percentage };
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading analytics data...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        {debugInfo && (
          <DebugInfo>
            <strong>Debug Information:</strong>
            <pre>{debugInfo}</pre>
          </DebugInfo>
        )}
        <div style={{ display: 'flex', gap: theme.spacing.md }}>
          <RefreshButton onClick={loadAnalytics}>
            Retry
          </RefreshButton>
          {error.includes('Invalid time value') && (
            <RefreshButton 
              onClick={handleCleanup}
              disabled={cleanupLoading}
              style={{ background: theme.colors.doryBlue }}
            >
              {cleanupLoading ? 'Fixing Data...' : 'Fix Invalid Dates'}
            </RefreshButton>
          )}
        </div>
      </Container>
    );
  }

  if (!analytics || Object.keys(analytics).length === 0) {
    return (
      <Container>
        <ErrorMessage>No production analytics data available</ErrorMessage>
        <div style={{ marginBottom: theme.spacing.lg }}>
          <p style={{ 
            fontFamily: theme.typography.bodyFont, 
            fontSize: theme.typography.sizes.small, 
            color: theme.colors.textLight,
            margin: 0,
            textAlign: 'center'
          }}>
            Analytics only shows production users (localhost sessions are excluded)
          </p>
        </div>
        <RefreshButton onClick={loadAnalytics}>
          Refresh
        </RefreshButton>
      </Container>
    );
  }

  const analyticsArray = Object.values(analytics).sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  const maxFunnelStarts = getMaxValue(analytics, 'funnelStarts');
  const maxAverageVisits = getMaxValue(analytics, 'averageScreenVisits');
  const totals = calculateTotals();
  const oneScreenStats = calculateOneScreenStats();

  return (
    <Container>
      <Header>
        <Title>Funnel Analytics</Title>
        <Subtitle>Daily funnel starts and average screen visits over the last 30 days including today (production users only)</Subtitle>
        <RefreshButton onClick={loadAnalytics}>
          Refresh Data
        </RefreshButton>
      </Header>

      <ChartsContainer>
        <ChartCard>
          <ChartTitle>Daily Funnel Starts</ChartTitle>
          <ChartContainer>
            {analyticsArray.map((day) => (
              <BarContainer key={day.date}>
                <BarValue>{day.funnelStarts}</BarValue>
                <Bar 
                  $isFunnelStarts={true}
                  style={{ 
                    height: `${getBarHeight(day.funnelStarts, maxFunnelStarts)}px`
                  }}
                />
                <BarLabel>{formatDate(day.date)}</BarLabel>
              </BarContainer>
            ))}
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Average Screen Visits per User</ChartTitle>
          <ChartContainer>
            {analyticsArray.map((day) => (
              <BarContainer key={day.date}>
                <BarValue>{day.averageScreenVisits.toFixed(1)}</BarValue>
                <Bar 
                  $isFunnelStarts={false}
                  style={{ 
                    height: `${getBarHeight(day.averageScreenVisits, maxAverageVisits)}px`
                  }}
                />
                <BarLabel>{formatDate(day.date)}</BarLabel>
              </BarContainer>
            ))}
          </ChartContainer>
        </ChartCard>
      </ChartsContainer>

      <FullWidthChartContainer>
        <ChartCard>
          <ChartTitle>Screen Completion Frequency</ChartTitle>
          <ChartContainer>
            {screenCompletionFrequency && Object.keys(screenCompletionFrequency).length > 0 ? (
              Object.keys(screenCompletionFrequency)
                .map(Number)
                .sort((a, b) => a - b)
                .filter(screenCount => screenCount > 0) // Exclude 0 completed screens (1-screen visits)
                .map((screenCount) => (
                  <BarContainer key={screenCount}>
                    <BarValue>{screenCompletionFrequency[screenCount]}</BarValue>
                    <Bar 
                      $isFunnelStarts={false}
                      style={{ 
                        height: `${getBarHeight(screenCompletionFrequency[screenCount], Math.max(...Object.values(screenCompletionFrequency)), 27)}px`
                      }}
                    />
                    <BarLabel>{screenCount + 1} screens</BarLabel>
                  </BarContainer>
                ))
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: theme.colors.textLight, 
                fontFamily: theme.typography.bodyFont,
                fontSize: theme.typography.sizes.body,
                padding: theme.spacing.xl
              }}>
                No screen completion data available
              </div>
            )}
          </ChartContainer>
        </ChartCard>
      </FullWidthChartContainer>

      <SummaryStats>
        <StatCard>
          <StatValue>{totals.totalStarts}</StatValue>
          <StatLabel>Total Funnel Starts (30 days)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totals.totalVisits}</StatValue>
          <StatLabel>Total Screen Visits</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totals.averageVisits}</StatValue>
          <StatLabel>Average Visits per User</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{Math.round((totals.totalStarts / 30) * 100) / 100}</StatValue>
          <StatLabel>Daily Average Starts</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{oneScreenStats.count}</StatValue>
          <StatLabel>One-Screen Visits ({oneScreenStats.percentage}% of all visitors)</StatLabel>
        </StatCard>
      </SummaryStats>
    </Container>
  );
};

export default AnalyticsScreen;
